import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type { InputNode } from '../types';
import { App } from 'antd';
import {
  PLACEHOLDER_THUMB,
  PlayIcon,
  ProgressRing,
  VideoPreviewTooltip,
  extractUrlFromText,
  extractVideoFirstFrameFromUrl,
} from './videoShared';
import { parseStepsTooltip } from '../utils/stepsTooltip';
import { StepsTutorial } from './StepsTutorial';
import { InfoIconTooltip } from './InfoIconTooltip';

type Props = {
  node: InputNode;
  value: string | undefined;
  thumbnail?: string;
  onChange: (key: string, value: string) => void;
  onThumbnailChange?: (key: string, thumbnail: string) => void;
  videoUrlParse?: (params: {
    url: string;
  }) => Promise<{ videoUrl: string; thumbnail: string }>;
  onParsingChange?: (key: string, parsing: boolean) => void;
  disabled?: boolean;
  error?: string;
};

// 基础 input 样式；width 由 mirror span 测量后动态注入（同 MultiVariableNode），
// 让空态宽度按 placeholder/value 内容自然撑开，不再被固定 minWidth 截成省略号。
// inline-block(不是 inline)— text-overflow:ellipsis 仅对 block container 生效。
const INPUT_STYLE_BASE: React.CSSProperties = {
  display: 'inline-block',
  border: 0,
  background: 'transparent',
  outline: 'none',
  padding: 0,
  margin: 0,
  font: 'inherit',
  color: 'inherit',
  minWidth: 40,
  boxSizing: 'border-box',
  verticalAlign: 'middle',
};

const normalizeTooltip = (t: InputNode['tooltip']): string => {
  if (!t) return '';
  return typeof t === 'string' ? t : JSON.stringify(t);
};

/**
 * V1 parity (RichTextEditor/nodes/video/render.ts + hooks.ts):
 * - 状态机：empty(input) → loading(进度环+灰底缩略图) → success(缩略图+播放+文件名+移除) / error
 * - 抖音口令/分享文本提取真实 URL（粘贴时）
 * - 假进度环（解析慢时填充视觉空白）
 * - tooltip 模式：empty 渲染 .variable-info-icon（V1 line 47-48）
 * - required 模式：所有 state 外套 .exclusion-wrapper.required-upload-group + ⛤
 * - chip 所有状态都带 data-tooltip + data-group(exclusionGroup) attribute
 */
export const VideoUrlNode: React.FC<Props> = React.memo(
  ({
    node,
    value,
    thumbnail,
    onChange,
    onThumbnailChange,
    videoUrlParse,
    onParsingChange,
    disabled,
    error,
  }) => {
    const [status, setStatus] = useState<'idle' | 'loading' | 'success'>(
      'idle',
    );
    const { message } = App.useApp();
    const [percent, setPercent] = useState(0);
    const [fileName, setFileName] = useState('');
    const fakeTimerRef = useRef<number | null>(null);
    // 解析代次：每次开始解析 / 移除时自增。异步首帧回填用它判断「这次回填是否还有效」，
    // 等价 V1 hooks.ts:182-184 的 `cur.status !== 'success'` 守卫（避免给已被移除/重选的
    // 节点回填旧缩略图），但用 ref token 表达而非读 DOM/外部 state。
    const parseGenRef = useRef(0);

    // 隐藏 mirror span 测 value/placeholder 实际渲染宽度，给 input 显式 width，
    // 让空态 chip 按内容自然撑开（同 MultiVariableNode），避免长 placeholder 被
    // ellipsis 截成省略号。带 icon 时让位由 chip padding-right 负责，input 不重复 reserve。
    const mirrorRef = useRef<HTMLSpanElement>(null);
    const measureFor = value || node.placeholder || '';
    const [measuredWidth, setMeasuredWidth] = useState(0);
    const remeasure = useCallback(() => {
      if (mirrorRef.current) setMeasuredWidth(mirrorRef.current.offsetWidth);
    }, []);
    useLayoutEffect(() => {
      remeasure();
      // CJK / 自定义字体异步加载时首帧测窄（fallback 窄字体）→ input 被压窄 → ellipsis 误截。
      // fonts.ready 不可靠（可能在字体 swap 前就 resolve），改用 ResizeObserver 监听 mirror
      // 实际渲染宽度变化：字体何时 swap 都能捕获并校正（同 UploadNode useIsWrapped 范式）。
      const el = mirrorRef.current;
      if (!el || typeof ResizeObserver === 'undefined') return;
      const ro = new ResizeObserver(() => remeasure());
      ro.observe(el);
      return () => ro.disconnect();
    }, [measureFor, remeasure]);
    // +2px 余量：mirror 与 input 占位符渲染存在亚像素差，取精确值时 scrollWidth 可能
    // 比 clientWidth 大 1px → 误触发 ellipsis。
    const inputWidth = measuredWidth > 0 ? Math.max(40, measuredWidth + 2) : 40;

    // steps tooltip 走「查看教程」按钮：empty-state 不渲染 info-icon（tooltip 置空），
    // wrap 在 chip 同级追加 StepsTutorial（有 value/缩略图时隐藏）。
    const stepsTooltip = parseStepsTooltip(node.tooltip);
    const isSteps = !!stepsTooltip;
    const tooltip = isSteps ? '' : normalizeTooltip(node.tooltip);
    const hasContent = !!(value || thumbnail);

    const stopFakeProgress = useCallback(() => {
      if (fakeTimerRef.current) {
        window.clearInterval(fakeTimerRef.current);
        fakeTimerRef.current = null;
      }
    }, []);

    const startFakeProgress = useCallback(() => {
      stopFakeProgress();
      let p = 0;
      fakeTimerRef.current = window.setInterval(() => {
        p = Math.min(p + Math.random() * 15 + 5, 90);
        setPercent(p);
      }, 400);
    }, [stopFakeProgress]);

    useEffect(() => () => stopFakeProgress(), [stopFakeProgress]);

    const doParse = useCallback(
      async (rawText: string) => {
        if (!videoUrlParse) return;
        const text = rawText.trim();
        if (!text) return;
        const url = extractUrlFromText(text);
        const gen = (parseGenRef.current += 1);
        setStatus('loading');
        setPercent(0);
        setFileName(url);
        startFakeProgress();
        onParsingChange?.(node.key!, true);
        try {
          const r = await videoUrlParse({ url });
          stopFakeProgress();
          onChange(node.key!, r.videoUrl);
          onThumbnailChange?.(node.key!, r.thumbnail || '');
          setStatus('success');
          if (!r.thumbnail && r.videoUrl) {
            const proxyUrl = `/api/download-report-pdf?url=${encodeURIComponent(r.videoUrl)}`;
            const frame = await extractVideoFirstFrameFromUrl(proxyUrl);
            if (frame && parseGenRef.current === gen) {
              onThumbnailChange?.(node.key!, frame);
            }
          }
        } catch (e) {
          // 解析失败：不进 error 红框态，直接清空 value/缩略图/文件名退回空输入框
          // （产品要求「错了就回默认状态」），仅用 toast 告知失败原因，避免静默吞掉。
          stopFakeProgress();
          parseGenRef.current += 1; // 作废可能在途的首帧回填
          onChange(node.key!, '');
          onThumbnailChange?.(node.key!, '');
          setFileName('');
          setStatus('idle');
          message.error(e instanceof Error ? e.message : '视频解析失败，请检查链接是否正确');
        } finally {
          onParsingChange?.(node.key!, false);
        }
      },
      [
        videoUrlParse,
        node.key,
        onChange,
        onThumbnailChange,
        onParsingChange,
        startFakeProgress,
        stopFakeProgress,
        message,
      ],
    );

    const onBlur = useCallback(() => {
      if (status === 'loading') return;
      if (status === 'success' || thumbnail) return; // 已经 success，不重复解析
      const raw = value ?? '';
      if (!raw.trim()) return;
      void doParse(raw);
    }, [status, thumbnail, value, doParse]);

    const onPaste = useCallback(
      (e: React.ClipboardEvent<HTMLInputElement>) => {
        if (!videoUrlParse) return;
        const text = e.clipboardData?.getData('text') ?? '';
        if (!text.trim()) return;
        e.preventDefault();
        const url = extractUrlFromText(text);
        onChange(node.key!, url);
        void doParse(url);
      },
      [videoUrlParse, node.key, onChange, doParse],
    );

    const remove = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        // 自增代次 → 作废可能在途的首帧回填（避免回填到已移除节点）
        parseGenRef.current += 1;
        onChange(node.key!, '');
        onThumbnailChange?.(node.key!, '');
        setFileName('');
        setStatus('idle');
      },
      [node.key, onChange, onThumbnailChange],
    );

    // V1 parity:required 模式所有 state 外套 .exclusion-wrapper.required-upload-group + ⛤
    // steps tooltip 时在 chip 同级追加「查看教程」按钮（有内容时隐藏）。
    const wrap = (chip: React.ReactElement): React.ReactElement => {
      const tutorial =
        isSteps && stepsTooltip ? (
          <StepsTutorial data={stepsTooltip} hidden={hasContent} />
        ) : null;
      if (!node.required) {
        return tutorial ? (
          <>
            {chip}
            {tutorial}
          </>
        ) : (
          chip
        );
      }
      return (
        <>
          <span
            className="exclusion-wrapper required-upload-group"
            data-group={node.exclusionGroup}
          >
            <span className="rte-text-required">
              <i className="iconfont icon-bitian" style={{ fontSize: 10 }} />
            </span>
            {chip}
          </span>
          {tutorial}
        </>
      );
    };

    // success: 解析出 videoUrl 即进入（V1 render.ts:49），thumbnail 可选。
    // - status==='success'：本组件解析成功（无论有无 thumbnail）
    // - thumbnail truthy：外部回填/重载已有缩略图
    // 无 thumbnail 时显示占位视频图标（V1 render.ts:54）。
    const videoUrl = value ?? '';
    if ((status === 'success' || thumbnail) && videoUrl) {
      const displayName = fileName || value || '';
      const chip = (
        <span
          className={`video-chip ${error ? 'rte-video-error' : ''}`}
          data-type="video-url"
          data-key={node.key}
          data-tooltip={tooltip || undefined}
          data-group={node.exclusionGroup}
        >
          <span className="video-inner-wrapper video-url-inner has-video">
            <span className="video-thumbnail-wrapper">
              {thumbnail ? (
                <img
                  src={thumbnail}
                  className="video-thumbnail rte-video-thumb"
                  alt="thumbnail"
                />
              ) : (
                <span className="video-thumbnail video-thumbnail-placeholder">
                  <i
                    className="iconfont icon-shipin"
                    style={{ fontSize: 20, color: '#8c8c8c' }}
                  />
                </span>
              )}
              <PlayIcon />
            </span>
            <span className="video-label" data-file-name={displayName}>
              {displayName}
            </span>
            <span className="video-remove-btn rte-video-remove" title="移除视频" onClick={remove}>
              ×
            </span>
          </span>
        </span>
      );
      // 缺口 #26：hover 已填视频 chip 弹 <video> 预览
      return wrap(<VideoPreviewTooltip videoUrl={videoUrl}>{chip}</VideoPreviewTooltip>);
    }

    // loading: 进度环 + 灰底缩略图 + "解析中..."
    if (status === 'loading') {
      return wrap(
        <span
          className={`video-chip ${error ? 'rte-video-error' : ''}`}
          data-type="video-url"
          data-key={node.key}
          data-tooltip={tooltip || undefined}
          data-group={node.exclusionGroup}
        >
          <span className="video-inner-wrapper video-upload-inner video-uploading">
            <span className="video-thumbnail-wrapper">
              <img className="video-thumbnail" src={PLACEHOLDER_THUMB} alt="thumbnail" />
              <span className="video-thumbnail-overlay" />
              <ProgressRing percent={percent} />
            </span>
            <span className="video-label">解析中...</span>
          </span>
        </span>,
      );
    }

    // error: 解析失败不再渲染红框态，doParse 的 catch 已直接清空退回 empty + toast 提示。

    // empty: input + 可选 tooltip info-icon
    return wrap(
      <span
        className={[
          'variable-chip',
          'rte-video-url',
          !value ? 'show-placeholder' : '',
          tooltip ? 'rte-variable-with-icon' : '',
          error ? 'rte-video-error' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        data-type="video-url"
        data-key={node.key}
        data-tooltip={tooltip || undefined}
        data-group={node.exclusionGroup}
      >
        {/* hidden mirror，absolute 不影响布局，font:inherit 与 input 同尺寸 →
            offsetWidth 等于 input value/placeholder 实际渲染宽 */}
        <span
          ref={mirrorRef}
          aria-hidden="true"
          style={{
            position: 'absolute',
            visibility: 'hidden',
            whiteSpace: 'pre',
            font: 'inherit',
            pointerEvents: 'none',
            top: 0,
            left: 0,
          }}
        >
          {measureFor}
        </span>
        <input
          data-type="video-url"
          data-key={node.key}
          value={value ?? ''}
          onChange={(e) => onChange(node.key!, e.target.value)}
          onBlur={onBlur}
          onPaste={onPaste}
          disabled={disabled}
          placeholder={node.placeholder ?? '请粘贴想要分析的抖音视频链接'}
          style={{ ...INPUT_STYLE_BASE, width: inputWidth }}
        />
        {tooltip && (
          <InfoIconTooltip
            tooltip={tooltip}
            className="variable-info-icon"
            dataVariableKey={node.key}
          />
        )}
      </span>,
    );
  },
);
VideoUrlNode.displayName = 'VideoUrlNode';
